import { useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';

import { usePathname } from 'src/routes/hooks';
import { useResponsive } from 'src/hooks/use-responsive';

import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';

import Iconify from 'src/components/iconify';
import IconButton from '@mui/material/IconButton';
import { useWhitelabelStore } from 'src/store/whitelabel/whitelabel.store';
import { STORAGE_KEY_NAV_FAVORITES } from 'src/config/storage-keys';
import { NAV } from '../config-layout';
import type { NavSection } from './config-navigation';
import NavToggleButton from '../common/nav-toggle-button';
import { getStoredFavoriteIds, groupNavDataByFavorites, persistFavoriteIds } from './nav-favorites';

// ----------------------------------------------------------------------

type Props = {
  data: NavSection[];
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ data, openNav, onCloseNav }: Props) {
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const loginImageUrl = useWhitelabelStore(
    (state) => state.whitelabel?.loginImage ?? '/logo/logo-nectar-horizontal-laranja.png'
  );
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [collapsedDesktop] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<string[]>(getStoredFavoriteIds);

  const displayedData = useMemo(() => groupNavDataByFavorites(data, favoriteIds), [data, favoriteIds]);

  const handleToggleAllCollapse = () => {
    setAllCollapsed((prev) => !prev);
  };

  const handleToggleFavorite = useCallback((favoriteId: string) => {
    setFavoriteIds((currentFavoriteIds) => {
      const nextFavoriteIds = currentFavoriteIds.includes(favoriteId)
        ? currentFavoriteIds.filter((id) => id !== favoriteId)
        : [...currentFavoriteIds, favoriteId];

      persistFavoriteIds(nextFavoriteIds);

      return nextFavoriteIds;
    });
  }, []);

  const handleClearFavorites = useCallback(() => {
    setFavoriteIds(() => {
      const nextFavoriteIds: string[] = [];
      persistFavoriteIds(nextFavoriteIds);

      return nextFavoriteIds;
    });
  }, []);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY_NAV_FAVORITES) {
        setFavoriteIds(getStoredFavoriteIds());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, ml: 3, mb: 1, mr: 2, height: 40 }}>
        <Logo
          width={!lgUp ? 132 : 118}
          height={!lgUp ? 32 : 28}
          customLogo={loginImageUrl}
          sx={{ minWidth: !lgUp ? 132 : 118, minHeight: !lgUp ? 32 : 28 }}
        />
        <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 40 }}>
          <IconButton
            onClick={handleToggleAllCollapse}
            sx={{ width: 24, height: 24, borderRadius: 5, border: '1px solid', borderColor: 'grey.300', p: 0, mr: 2.5 }}
          >
            <Iconify icon="eva:minus-outline" width={18} sx={{ color: 'grey.700' }} />
          </IconButton>
        </Stack>
      </Stack>
      <NavSectionVertical
        data={displayedData}
        slotProps={{
          allCollapsed,
          collapsed: collapsedDesktop && lgUp,
          favoriteIds,
          onToggleFavorite: handleToggleFavorite,
          onClearFavorites: handleClearFavorites,
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ pb: { xs: 2, md: 4 } }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: collapsedDesktop ? 80 : NAV.W_VERTICAL },
        transition: 'width 0.2s',
      }}
    >
      <NavToggleButton />
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: collapsedDesktop ? 80 : NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
            transition: 'width 0.2s',
            backgroundColor: 'background.paper',
            zIndex: 1200,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
